import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, CreditCard, RefreshCw, XCircle, HelpCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Footer from '../components/Footer';

const RefundPolicyPage = () => {
  const { isAuthenticated } = useAuth();
  const homeRoute = isAuthenticated ? '/dashboard' : '/';
  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to={homeRoute} className="flex items-center text-gray-600 hover:text-gray-800">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Link>
          <Link to={homeRoute} className="flex items-center space-x-2">
            <Sparkles className="w-6 h-6 text-rose-400" />
            <span className="text-xl font-bold bg-gradient-to-r from-rose-500 to-orange-500 bg-clip-text text-transparent">VirtualFit</span>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12" data-testid="refund-policy-page">
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Refund & Cancellation Policy</h1>
        <p className="text-gray-500 mb-8">Last updated: March 12, 2026</p>

        <div className="prose prose-gray max-w-none space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Subscription Plans</h2>
            <p className="text-gray-600">
              VirtualFit offers monthly subscription plans for Brands, Influencers, and Personal users. All subscriptions are billed on a recurring monthly basis through Stripe, our payment processor.
            </p>
          </section>

          <section>
            <div className="flex items-center gap-2 mb-3">
              <XCircle className="w-5 h-5 text-rose-500" />
              <h2 className="text-xl font-semibold text-gray-900 m-0">Cancellation</h2>
            </div>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>You may cancel your subscription at any time from your <Link to="/subscription" className="text-rose-500 hover:underline">Subscription Management</Link> page or Settings.</li>
              <li>Upon cancellation, your subscription remains active until the end of your current billing period. You will not be charged again.</li>
              <li>After the billing period ends, your account will be downgraded and you will lose access to paid features.</li>
              <li>Your portfolio, collaborations, and generated images will be retained for 30 days after downgrade, after which they may be permanently deleted.</li>
            </ul>
          </section>

          <section>
            <div className="flex items-center gap-2 mb-3">
              <CreditCard className="w-5 h-5 text-rose-500" />
              <h2 className="text-xl font-semibold text-gray-900 m-0">Refunds</h2>
            </div>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li><strong>Within 48 hours of initial purchase:</strong> If you are unsatisfied with VirtualFit, you may request a full refund of your first payment by contacting <a href="mailto:hello@virtualfit.work" className="text-rose-500 hover:underline">hello@virtualfit.work</a> within 48 hours of your first subscription payment.</li>
              <li><strong>After 48 hours:</strong> We do not offer refunds for partially used billing periods. You may cancel at any time, and your subscription will remain active until the end of the current billing cycle.</li>
              <li><strong>Renewals:</strong> Recurring monthly charges are non-refundable. Please cancel before your renewal date if you do not wish to be charged.</li>
              <li><strong>Campaign funds:</strong> Funds deposited into Brand Campaign wallets are non-refundable once a campaign has been created. Unused wallet funds from campaigns that have not been launched may be refunded upon request.</li>
            </ul>
          </section>

          <section>
            <div className="flex items-center gap-2 mb-3">
              <RefreshCw className="w-5 h-5 text-rose-500" />
              <h2 className="text-xl font-semibold text-gray-900 m-0">Reactivation</h2>
            </div>
            <p className="text-gray-600">
              If you cancel and later wish to resubscribe, you may reactivate your subscription at any time from the Subscription page. Your previous data may still be available if within the 30-day retention window.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Free Trial</h2>
            <p className="text-gray-600">
              VirtualFit offers a free trial that does not require payment information. No charges are incurred during the free trial, and no refund is applicable.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Chargebacks & Disputes</h2>
            <p className="text-gray-600">
              If you believe a charge is unauthorized or incorrect, please contact us at <a href="mailto:hello@virtualfit.work" className="text-rose-500 hover:underline">hello@virtualfit.work</a> before filing a chargeback with your bank. We will work with you to resolve the issue promptly. Filing a chargeback without prior communication may result in account suspension.
            </p>
          </section>

          <section>
            <div className="flex items-center gap-2 mb-3">
              <HelpCircle className="w-5 h-5 text-rose-500" />
              <h2 className="text-xl font-semibold text-gray-900 m-0">How to Request a Refund</h2>
            </div>
            <ol className="list-decimal pl-6 text-gray-600 space-y-2">
              <li>Email <a href="mailto:hello@virtualfit.work" className="text-rose-500 hover:underline">hello@virtualfit.work</a> with the subject line "Refund Request"</li>
              <li>Include your registered email address and reason for the request</li>
              <li>We will process eligible refunds within 5-10 business days</li>
            </ol>
          </section>

          <section className="bg-gray-50 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Questions?</h2>
            <p className="text-gray-600">
              If you have questions about our refund or cancellation policy, contact us at <a href="mailto:hello@virtualfit.work" className="text-rose-500 hover:underline">hello@virtualfit.work</a> or visit our <Link to="/contact" className="text-rose-500 hover:underline">Contact</Link> page.
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default RefundPolicyPage;
